/**
 * Project Form Component
 *
 * Modal form for creating and editing projects
 * Handles validation for required fields
 *
 * Fields:
 * - Project Name (required)
 * - Project ID (required for create, disabled for edit)
 * - Description
 * - Status
 * - Budget and budget period
 * - Owner email
 * - Cost center
 * - Tags (JSON)
 * - Start/End dates
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Label, TextInput, Textarea, Select, Spinner, Alert } from 'flowbite-react';
import { HiFolderOpen, HiX } from 'react-icons/hi';

import { createProject, updateProject, type Project, type CreateProjectRequest, type UpdateProjectRequest } from '../api/projects';

interface ProjectFormProps {
  clientId: string;
  companyId: string;
  project?: Project | null; // If provided, edit mode; otherwise create mode
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (project: Project) => void;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({
  clientId,
  companyId,
  project,
  isOpen,
  onClose,
  onSuccess
}) => {
  const isEditMode = !!project;

  // Form state
  const [formData, setFormData] = useState({
    Project_ID: '',
    Project_Name: '',
    Project_Description: '',
    Project_Status: 'active' as 'active' | 'archived' | 'on_hold' | 'completed',
    Budget_USD: '',
    Budget_Period: '' as '' | 'monthly' | 'quarterly' | 'annual' | 'total',
    Owner_Email: '',
    Cost_Center: '',
    Tags: '',
    Start_Date: '',
    End_Date: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Initialize form data when project changes
  useEffect(() => {
    if (project) {
      setFormData({
        Project_ID: project.Project_ID || '',
        Project_Name: project.Project_Name || '',
        Project_Description: project.Project_Description || '',
        Project_Status: project.Project_Status || 'active',
        Budget_USD: project.Budget_USD?.toString() || '',
        Budget_Period: project.Budget_Period || '',
        Owner_Email: project.Owner_Email || '',
        Cost_Center: project.Cost_Center || '',
        Tags: project.Tags ? JSON.stringify(project.Tags, null, 2) : '',
        Start_Date: project.Start_Date || '',
        End_Date: project.End_Date || ''
      });
    } else {
      // Reset form for create mode
      setFormData({
        Project_ID: '',
        Project_Name: '',
        Project_Description: '',
        Project_Status: 'active',
        Budget_USD: '',
        Budget_Period: '',
        Owner_Email: '',
        Cost_Center: '',
        Tags: '',
        Start_Date: '',
        End_Date: ''
      });
    }
    setError(null);
    setValidationErrors({});
  }, [project, isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.Project_Name.trim()) {
      errors.Project_Name = 'Project name is required';
    }

    if (!isEditMode && !formData.Project_ID.trim()) {
      errors.Project_ID = 'Project ID is required';
    }

    // Validate Project_ID format (alphanumeric, hyphens, underscores)
    if (!isEditMode && formData.Project_ID && !/^[a-zA-Z0-9_-]+$/.test(formData.Project_ID)) {
      errors.Project_ID = 'Project ID can only contain letters, numbers, hyphens, and underscores';
    }

    // Validate email if provided
    if (formData.Owner_Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Owner_Email)) {
      errors.Owner_Email = 'Invalid email format';
    }

    // Validate budget if provided
    if (formData.Budget_USD && (isNaN(parseFloat(formData.Budget_USD)) || parseFloat(formData.Budget_USD) < 0)) {
      errors.Budget_USD = 'Budget must be a positive number';
    }

    // Validate dates
    if (formData.Start_Date && formData.End_Date && new Date(formData.Start_Date) > new Date(formData.End_Date)) {
      errors.End_Date = 'End date must be after start date';
    }

    // Validate Tags JSON if provided
    if (formData.Tags.trim()) {
      try {
        JSON.parse(formData.Tags);
      } catch (e) {
        errors.Tags = 'Tags must be valid JSON';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditMode) {
        // Update existing project
        const updates: UpdateProjectRequest = {
          Project_Name: formData.Project_Name,
          Project_Description: formData.Project_Description || undefined,
          Project_Status: formData.Project_Status,
          Budget_USD: formData.Budget_USD ? parseFloat(formData.Budget_USD) : undefined,
          Budget_Period: formData.Budget_Period || undefined,
          Owner_Email: formData.Owner_Email || undefined,
          Cost_Center: formData.Cost_Center || undefined,
          Tags: formData.Tags.trim() ? JSON.parse(formData.Tags) : undefined,
          Start_Date: formData.Start_Date || undefined,
          End_Date: formData.End_Date || undefined
        };

        const response = await updateProject(clientId, companyId, project!.Project_ID, updates);

        if (!response.success) {
          throw new Error(response.message || 'Failed to update project');
        }

        onSuccess?.(response.data.Records[0]);
      } else {
        // Create new project
        const newProject: CreateProjectRequest = {
          Client_ID: clientId,
          Company_ID: companyId,
          Project_ID: formData.Project_ID,
          Project_Name: formData.Project_Name,
          Project_Description: formData.Project_Description || undefined,
          Project_Status: formData.Project_Status,
          Budget_USD: formData.Budget_USD ? parseFloat(formData.Budget_USD) : undefined,
          Budget_Period: formData.Budget_Period || undefined,
          Owner_Email: formData.Owner_Email || undefined,
          Cost_Center: formData.Cost_Center || undefined,
          Tags: formData.Tags.trim() ? JSON.parse(formData.Tags) : undefined,
          Start_Date: formData.Start_Date || undefined,
          End_Date: formData.End_Date || undefined
        };

        const response = await createProject(newProject);

        if (!response.success) {
          throw new Error(response.message || 'Failed to create project');
        }

        onSuccess?.(response.data.Records[0]);
      }

      onClose();
    } catch (err) {
      console.error('[ProjectForm] Error submitting form:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <div className="flex items-center space-x-2">
          <HiFolderOpen className="w-6 h-6 text-blue-600" />
          <span>{isEditMode ? 'Edit Project' : 'Create New Project'}</span>
        </div>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert color="failure" className="mb-4">
            <p className="text-sm">{error}</p>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Project ID (required for create, disabled for edit) */}
          <div>
            <Label htmlFor="project-id" value="Project ID *" />
            <TextInput
              id="project-id"
              value={formData.Project_ID}
              onChange={(e) => handleInputChange('Project_ID', e.target.value)}
              disabled={isEditMode}
              placeholder="e.g., proj-marketing-2025"
              color={validationErrors.Project_ID ? 'failure' : undefined}
              helperText={validationErrors.Project_ID || 'Unique identifier (alphanumeric, hyphens, underscores)'}
              required={!isEditMode}
            />
          </div>

          {/* Project Name */}
          <div>
            <Label htmlFor="project-name" value="Project Name *" />
            <TextInput
              id="project-name"
              value={formData.Project_Name}
              onChange={(e) => handleInputChange('Project_Name', e.target.value)}
              placeholder="e.g., Marketing Campaign 2025"
              color={validationErrors.Project_Name ? 'failure' : undefined}
              helperText={validationErrors.Project_Name}
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="project-description" value="Description" />
            <Textarea
              id="project-description"
              value={formData.Project_Description}
              onChange={(e) => handleInputChange('Project_Description', e.target.value)}
              placeholder="Describe the project goals and scope..."
              rows={3}
            />
          </div>

          {/* Status */}
          <div>
            <Label htmlFor="project-status" value="Status" />
            <Select
              id="project-status"
              value={formData.Project_Status}
              onChange={(e) => handleInputChange('Project_Status', e.target.value)}
            >
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </Select>
          </div>

          {/* Budget */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="budget-usd" value="Budget (USD)" />
              <TextInput
                id="budget-usd"
                type="number"
                step="0.01"
                min="0"
                value={formData.Budget_USD}
                onChange={(e) => handleInputChange('Budget_USD', e.target.value)}
                placeholder="0.00"
                color={validationErrors.Budget_USD ? 'failure' : undefined}
                helperText={validationErrors.Budget_USD}
              />
            </div>
            <div>
              <Label htmlFor="budget-period" value="Budget Period" />
              <Select
                id="budget-period"
                value={formData.Budget_Period}
                onChange={(e) => handleInputChange('Budget_Period', e.target.value)}
              >
                <option value="">Select period...</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
                <option value="total">Total (Project Lifetime)</option>
              </Select>
            </div>
          </div>

          {/* Owner Email */}
          <div>
            <Label htmlFor="owner-email" value="Owner Email" />
            <TextInput
              id="owner-email"
              type="email"
              value={formData.Owner_Email}
              onChange={(e) => handleInputChange('Owner_Email', e.target.value)}
              placeholder="owner@example.com"
              color={validationErrors.Owner_Email ? 'failure' : undefined}
              helperText={validationErrors.Owner_Email}
            />
          </div>

          {/* Cost Center */}
          <div>
            <Label htmlFor="cost-center" value="Cost Center" />
            <TextInput
              id="cost-center"
              value={formData.Cost_Center}
              onChange={(e) => handleInputChange('Cost_Center', e.target.value)}
              placeholder="e.g., CC-MKTG-001"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-date" value="Start Date" />
              <TextInput
                id="start-date"
                type="date"
                value={formData.Start_Date}
                onChange={(e) => handleInputChange('Start_Date', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end-date" value="End Date" />
              <TextInput
                id="end-date"
                type="date"
                value={formData.End_Date}
                onChange={(e) => handleInputChange('End_Date', e.target.value)}
                color={validationErrors.End_Date ? 'failure' : undefined}
                helperText={validationErrors.End_Date}
              />
            </div>
          </div>

          {/* Tags (JSON) */}
          <div>
            <Label htmlFor="tags" value="Tags (JSON)" />
            <Textarea
              id="tags"
              value={formData.Tags}
              onChange={(e) => handleInputChange('Tags', e.target.value)}
              placeholder='{"department": "marketing", "priority": "high"}'
              rows={3}
              color={validationErrors.Tags ? 'failure' : undefined}
              helperText={validationErrors.Tags || 'Optional: Key-value pairs in JSON format'}
            />
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end space-x-3 w-full">
          <Button color="gray" onClick={onClose} disabled={isSubmitting}>
            <HiX className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button color="blue" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                {isEditMode ? 'Update Project' : 'Create Project'}
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectForm;
